import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";
import { Severity, valuesExist } from "../scenarioValidator";
import type { CorrelationValidationGenerator } from "./correlationChecks";

export const correlationsAtSurfaceChecks: CorrelationValidationGenerator = (
  correlationLimits,
  correlationField,
  { calcs, wellProperties }
) => [
  {
    name: `${correlationField}_oil_gravity_surface`,
    message: `API Out of Range @ Surface`,
    severity: Severity.warn,
    rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE,
    isActive: valuesExist([
      correlationLimits.MIN_API,
      correlationLimits.MAX_API,
      calcs.basicData.value.oilAPI
    ]),
    isInvalid:
      calcs.basicData.value.oilAPI > correlationLimits.MAX_API ||
      calcs.basicData.value.oilAPI < correlationLimits.MIN_API
  },
  {
    name: `${correlationField}_gas_specific_gravity_surface`,
    message: `Gas Specific Gravity Out of Range @ Surface`,
    severity: Severity.warn,
    rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE,
    isActive: valuesExist([
      correlationLimits.MIN_GAS_SG,
      correlationLimits.MAX_GAS_SG,
      calcs.basicData.value.gasSpecificGravity
    ]),
    isInvalid:
      calcs.basicData.value.gasSpecificGravity > correlationLimits.MAX_GAS_SG ||
      calcs.basicData.value.gasSpecificGravity < correlationLimits.MIN_GAS_SG
  },
  {
    name: `${correlationField}_temperature_surface`,
    message: `Temperature Out of Range @ Surface`,
    severity: Severity.warn,
    rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE,
    isActive: valuesExist([
      correlationLimits.MIN_TEMP,
      correlationLimits.MAX_TEMP,
      wellProperties.value?.surface_temperature_fahrenheit
    ]),
    isInvalid:
      wellProperties.value?.surface_temperature_fahrenheit >
        correlationLimits.MAX_TEMP ||
      wellProperties.value?.surface_temperature_fahrenheit <
        correlationLimits.MIN_TEMP
  },
  {
    name: `${correlationField}_pressure_surface`,
    message: `Pressure Out of Range @ Surface`,
    severity: Severity.warn,
    rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE,
    isActive: valuesExist([
      correlationLimits.MIN_PRESS,
      correlationLimits.MAX_PRESS,
      calcs.wellFlowingPressureSurfacePSIA.value
    ]),
    isInvalid:
      calcs.wellFlowingPressureSurfacePSIA.value >
        correlationLimits.MAX_PRESS ||
      calcs.wellFlowingPressureSurfacePSIA.value < correlationLimits.MIN_PRESS
  },
  {
    name: `${correlationField}_oil_solution_gas_surface`,
    message: `Oil Solution Gas Out of Range @ Surface`,
    severity: Severity.warn,
    rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE,
    isActive: valuesExist([
      correlationLimits.MIN_RSO,
      correlationLimits.MAX_RSO,
      calcs.fluidProperties.fluidPropertiesAfterAllGasSeparationAtPump.value
        .oilPhaseGasSolubilitySCFSTB
    ]),
    isInvalid:
      calcs.fluidProperties.fluidPropertiesAfterAllGasSeparationAtPump.value
        .oilPhaseGasSolubilitySCFSTB > correlationLimits.MAX_RSO ||
      calcs.fluidProperties.fluidPropertiesAfterAllGasSeparationAtPump.value
        .oilPhaseGasSolubilitySCFSTB < correlationLimits.MIN_RSO
  },
  {
    name: `${correlationField}_bubblepoint_pressure_surface`,
    message: `Bubblepoint Pressure Out of Range @ Surface`,
    severity: Severity.warn,
    rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE,
    isActive: valuesExist([
      correlationLimits.MIN_PB,
      correlationLimits.MAX_PB,
      calcs.fluidProperties.fluidPropertiesAfterAllGasSeparationAtPump.value
        .bubblePointPressurePSIA
    ]),
    isInvalid:
      calcs.fluidProperties.fluidPropertiesAfterAllGasSeparationAtPump.value
        .bubblePointPressurePSIA > correlationLimits.MAX_PB ||
      calcs.fluidProperties.fluidPropertiesAfterAllGasSeparationAtPump.value
        .bubblePointPressurePSIA < correlationLimits.MIN_PB
  },
  {
    name: `${correlationField}_oil_formation_volume_factor_surface`,
    message: `Oil Formation Volume Factor Out of Range @ Surface`,
    severity: Severity.warn,
    rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE,
    isActive: valuesExist([
      correlationLimits.MIN_BO,
      correlationLimits.MAX_BO,
      calcs.fluidProperties.fluidPropertiesAfterAllGasSeparationAtPump.value
        .oilVolumeFactorBBLSTB
    ]),
    isInvalid:
      calcs.fluidProperties.fluidPropertiesAfterAllGasSeparationAtPump.value
        .oilVolumeFactorBBLSTB > correlationLimits.MAX_BO ||
      calcs.fluidProperties.fluidPropertiesAfterAllGasSeparationAtPump.value
        .oilVolumeFactorBBLSTB < correlationLimits.MIN_BO
  },
  {
    name: `${correlationField}_dead_oil_viscosity_surface`,
    message: `Dead Oil Viscosity Out of Range @ Surface`,
    severity: Severity.warn,
    rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE,
    isActive: valuesExist([
      correlationLimits.MIN_U_OD,
      correlationLimits.MAX_U_OD,
      calcs.fluidProperties.fluidPropertiesAfterAllGasSeparationAtPump.value
        .deadOilViscosityCP
    ]),
    isInvalid:
      calcs.fluidProperties.fluidPropertiesAfterAllGasSeparationAtPump.value
        .deadOilViscosityCP > correlationLimits.MAX_U_OD ||
      calcs.fluidProperties.fluidPropertiesAfterAllGasSeparationAtPump.value
        .deadOilViscosityCP < correlationLimits.MIN_U_OD
  },
  {
    name: `${correlationField}_live_oil_viscosity_surface`,
    message: `Live Oil Viscosity Out of Range @ Surface`,
    severity: Severity.warn,
    rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE,
    isActive: valuesExist([
      correlationLimits.MIN_U_O,
      correlationLimits.MAX_U_O,
      calcs.fluidProperties.fluidPropertiesAfterAllGasSeparationAtPump.value
        .oilViscosityCP
    ]),
    isInvalid:
      calcs.fluidProperties.fluidPropertiesAfterAllGasSeparationAtPump.value
        .oilViscosityCP > correlationLimits.MAX_U_O ||
      calcs.fluidProperties.fluidPropertiesAfterAllGasSeparationAtPump.value
        .oilViscosityCP < correlationLimits.MIN_U_O
  },
  {
    name: `${correlationField}_pseudo_reduced_pressure_surface`,
    message: `Pseudo-Reduced Pressure Out of Range @ Surface`,
    severity: Severity.warn,
    rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE,
    isActive: valuesExist([
      correlationLimits.MIN_PPR,
      correlationLimits.MAX_PPR,
      calcs.fluidProperties.fluidPropertiesAfterAllGasSeparationAtPump.value
        .psuedoCriticals.psuedoReducedPressure
    ]),
    isInvalid:
      calcs.fluidProperties.fluidPropertiesAfterAllGasSeparationAtPump.value
        .psuedoCriticals.psuedoReducedPressure > correlationLimits.MAX_PPR ||
      calcs.fluidProperties.fluidPropertiesAfterAllGasSeparationAtPump.value
        .psuedoCriticals.psuedoReducedPressure < correlationLimits.MIN_PPR
  },
  {
    name: `${correlationField}_pseudo_reduced_temperature_surface`,
    message: `Pseudo-Reduced Temperature Out of Range @ Surface`,
    severity: Severity.warn,
    rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE,
    isActive: valuesExist([
      correlationLimits.MIN_TPR,
      correlationLimits.MAX_TPR,
      calcs.fluidProperties.fluidPropertiesAfterAllGasSeparationAtPump.value
        .psuedoCriticals.psuedoReducedTemperature
    ]),
    isInvalid:
      calcs.fluidProperties.fluidPropertiesAfterAllGasSeparationAtPump.value
        .psuedoCriticals.psuedoReducedTemperature > correlationLimits.MAX_TPR ||
      calcs.fluidProperties.fluidPropertiesAfterAllGasSeparationAtPump.value
        .psuedoCriticals.psuedoReducedTemperature < correlationLimits.MIN_TPR
  }
];
